import React, { forwardRef, useContext, useEffect, useRef } from 'react'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getRoot,
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  KEY_ENTER_COMMAND,
  FOCUS_COMMAND,
  BLUR_COMMAND,
  COMMAND_PRIORITY_NORMAL
} from 'lexical'

import { ToolbarPlugin } from './ToolbarPlugin'
import { AppContext } from '../App'
import { fetchVoices } from './operations'
import { useAsyncError } from '../errorBoundary/useAsyncError'
import { setUserActiveInEditor } from './actions'

const Editor = forwardRef((props, ref) => {
  const { state, dispatch } = useContext(AppContext)
  const [editor] = useLexicalComposerContext()
  const throwAsyncError = useAsyncError()
  const editorContainerRef = useRef(null)

  useEffect(() => {
    const unregisterFocus = editor.registerCommand(
      FOCUS_COMMAND,
      () => {
        const editorInput = editorContainerRef.current?.querySelector('.editor-input')
        if (editorInput) {
          editorInput.style.overflowY = 'auto'
        }

        editor.setEditable(true)
        document.body.classList.add('body-no-scroll')
        dispatch(setUserActiveInEditor(true))
      },
      COMMAND_PRIORITY_NORMAL
    )

    const unregisterBlur = editor.registerCommand(
      BLUR_COMMAND,
      () => {
        const editorInput = editorContainerRef.current?.querySelector('.editor-input')
        if (editorInput) {
          editorInput.style.overflowY = 'hidden'
        }

        document.body.classList.remove('body-no-scroll')
      },
      COMMAND_PRIORITY_NORMAL
    )

    const unregisterEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        event.preventDefault()
        editor.update(() => {
          const selection = $getSelection()
          if (selection) {
            selection.insertLineBreak()
          }
        })
        return true
      },
      COMMAND_PRIORITY_NORMAL
    )

    return () => {
      unregisterFocus()
      unregisterBlur()
      unregisterEnter()
    }
  }, [editor])

  useEffect(() => {
    editor.setEditable(false)

    editor.update(
      () => {
        const root = $getRoot()
        root.clear()

        if (state.generationForm.text && state.generationForm.text !== 'null') {
          const paragraphNode = $createParagraphNode()
          const textNode = $createTextNode(state.generationForm.text)
          paragraphNode.append(textNode)
          root.append(paragraphNode)
        }
      },
      {
        onUpdate: () => {
          editor.setEditable(true)
        }
      }
    )
  }, [editor, state.generationForm.text, state.currentProject.projectId])

  const handleBlur = () => {
    editor.update(() => {
      const plainText = editor.getEditorState().read(() => {
        return $getRoot().getTextContent()
      })

      props.onBlur({ target: { value: plainText } })

      // NOTE: Fetch voices if the editor is empty or if it doesn't include 🇺🇸 or 🇬🇧 flags.
      // This ensures that the correct voice list is available depending on the user's language input.
      if (!plainText.trim() || !(plainText.includes('🇺🇸') || plainText.includes('🇬🇧'))) {
        fetchVoices(dispatch, throwAsyncError)
      }
    })
  }

  return (
    <div
      className={`editor-container ${
        state.editor.isFullscreen ? 'fullscreen' : ''
      }`}
      ref={editorContainerRef}
    >
      <ToolbarPlugin />
      <div className='editor-inner'>
        <RichTextPlugin
          contentEditable={
            <ContentEditable className='editor-input' onBlur={handleBlur} />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
      </div>
    </div>
  )
})

export { Editor }
